import {
  AppBar,
  Box,
  Collapse,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Toolbar,
  Typography,
} from '@mui/material';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import MenuIcon from '@mui/icons-material/Menu';
import LogoutIcon from '@mui/icons-material/Logout';
import FolderIcon from '@mui/icons-material/Folder';
import GroupsIcon from '@mui/icons-material/Groups';
import SettingsIcon from '@mui/icons-material/Settings';
import CategoryIcon from '@mui/icons-material/Category';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import PsychologyIcon from '@mui/icons-material/Psychology';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import HistoryIcon from '@mui/icons-material/History';
import HandymanIcon from '@mui/icons-material/Handyman';
import PaymentsIcon from '@mui/icons-material/Payments';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import SavingsIcon from '@mui/icons-material/Savings';
import AssessmentIcon from '@mui/icons-material/Assessment';
import RequestQuoteIcon from '@mui/icons-material/RequestQuote';
import DescriptionIcon from '@mui/icons-material/Description';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import PointOfSaleIcon from '@mui/icons-material/PointOfSale';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import CalculateIcon from '@mui/icons-material/Calculate';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import BadgeIcon from '@mui/icons-material/Badge';
import BeachAccessIcon from '@mui/icons-material/BeachAccess';
import PaidIcon from '@mui/icons-material/Paid';
import Diversity3Icon from '@mui/icons-material/Diversity3';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import { Fragment, useState, type PropsWithChildren, type ReactNode } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../auth/AuthContext';
import { useMe } from '../hooks/useOps';
import { KillSwitchBanner } from './KillSwitchBanner';
import type { Capability } from '../api/types';
import logo from '../assets/dikonek-logo.png';

const DRAWER_WIDTH = 232;

/** Items with a `cap` are shown only when the user's capability flag is true. */
const NAV: Array<{ to: string; label: string; icon: ReactNode; cap?: keyof Capability }> = [
  { to: '/projects', label: 'Projects', icon: <FolderIcon />, cap: 'canViewProjects' },
  { to: '/team', label: 'Team', icon: <GroupsIcon />, cap: 'canManageTeam' },
];

type NavItem = { to: string; label: string; icon: ReactNode; cap?: keyof Capability };
type NavGroup = { label: string; icon: ReactNode; items: NavItem[] };

/** Collapsible nav groups (catalog/config + the agent stack). */
const GROUPS: NavGroup[] = [
  {
    label: 'Finance',
    icon: <PaymentsIcon />,
    items: [
      { to: '/finance/quotations', label: 'Quotations', icon: <RequestQuoteIcon />, cap: 'canViewQuotations' },
      { to: '/finance/invoices', label: 'Invoices', icon: <DescriptionIcon />, cap: 'canViewInvoices' },
      { to: '/finance/expenses', label: 'Expenses', icon: <ReceiptLongIcon />, cap: 'canViewExpenses' },
      { to: '/finance/budgets', label: 'Budgets', icon: <SavingsIcon />, cap: 'canSeeCash' },
      { to: '/finance/petty-cash', label: 'Petty Cash', icon: <PointOfSaleIcon />, cap: 'canSeeCash' },
      { to: '/finance/statements', label: 'Statements', icon: <AssessmentIcon />, cap: 'canSeeCash' },
      { to: '/finance/tax-close', label: 'Tax Close', icon: <AccountBalanceIcon />, cap: 'canSeeCash' },
      { to: '/finance/tax-prep', label: 'Tax Prep', icon: <CalculateIcon />, cap: 'canSeeCash' },
      { to: '/finance/compliance', label: 'Compliance', icon: <EventAvailableIcon />, cap: 'canSeeCash' },
      { to: '/finance/approvals', label: 'Approvals', icon: <FactCheckIcon />, cap: 'canApprove' },
    ],
  },
  {
    label: 'HR',
    icon: <Diversity3Icon />,
    items: [
      { to: '/hr/people', label: 'People', icon: <BadgeIcon />, cap: 'canManageHr' },
      { to: '/hr/leave', label: 'Leave', icon: <BeachAccessIcon />, cap: 'canManageHr' },
      { to: '/hr/payroll', label: 'Payroll', icon: <PaidIcon />, cap: 'canRunPayroll' },
    ],
  },
  {
    label: 'Settings',
    icon: <SettingsIcon />,
    items: [{ to: '/settings/track-types', label: 'Track Types', icon: <CategoryIcon />, cap: 'canManageTrackTypes' }],
  },
  {
    label: 'Agent',
    icon: <SmartToyIcon />,
    items: [
      { to: '/agent/brain', label: 'Brain', icon: <PsychologyIcon />, cap: 'canManageBrain' },
      { to: '/agent/access', label: 'Access', icon: <VpnKeyIcon />, cap: 'canManageAgents' },
      { to: '/agent/activity', label: 'Activity', icon: <HistoryIcon />, cap: 'canManageAgents' },
      { to: '/agent/tools', label: 'Tools', icon: <HandymanIcon />, cap: 'canManageAgents' },
    ],
  },
];

export function AppShell({ children }: PropsWithChildren) {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user, logout } = useAuth();
  const { data: me } = useMe();
  const cap = me?.capability;
  const hasCap = (c?: keyof Capability) => !c || (cap ? cap[c] : false);
  const nav = NAV.filter((item) => hasCap(item.cap));
  const groups = GROUPS.map((g) => ({ ...g, items: g.items.filter((i) => hasCap(i.cap)) })).filter(
    (g) => g.items.length > 0,
  );
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>({});
  const [userMenuEl, setUserMenuEl] = useState<HTMLElement | null>(null);
  const [mobileOpen, setMobileOpen] = useState(false);

  const onLogout = () => {
    setUserMenuEl(null);
    logout();
    queryClient.clear();
    navigate('/login', { replace: true });
  };

  // Drawer body — shared between the permanent (md+) and temporary (mobile) drawers.
  // On mobile, tapping any nav link closes the temporary drawer.
  const drawerContent = (
    <>
      <Toolbar />
      <Box sx={{ overflow: 'auto' }}>
        <List>
          {nav.map((item) => {
            const active = item.to === '/' ? pathname === '/' : pathname.startsWith(item.to);
            return (
              <ListItemButton
                key={item.to}
                component={NavLink}
                to={item.to}
                selected={active}
                onClick={() => setMobileOpen(false)}
              >
                <ListItemIcon>{item.icon}</ListItemIcon>
                <ListItemText primary={item.label} />
              </ListItemButton>
            );
          })}

          {groups.map((g) => {
            const active = g.items.some((item) => pathname.startsWith(item.to));
            const open = openGroups[g.label] ?? active;
            return (
              <Fragment key={g.label}>
                <ListItemButton
                  onClick={() => setOpenGroups((s) => ({ ...s, [g.label]: !open }))}
                  selected={active && !open}
                >
                  <ListItemIcon>{g.icon}</ListItemIcon>
                  <ListItemText primary={g.label} />
                  {open ? <ExpandLess /> : <ExpandMore />}
                </ListItemButton>
                <Collapse in={open} timeout="auto" unmountOnExit>
                  <List component="div" disablePadding>
                    {g.items.map((item) => (
                      <ListItemButton
                        key={item.to}
                        component={NavLink}
                        to={item.to}
                        selected={pathname.startsWith(item.to)}
                        sx={{ pl: 4 }}
                        onClick={() => setMobileOpen(false)}
                      >
                        <ListItemIcon>{item.icon}</ListItemIcon>
                        <ListItemText primary={item.label} />
                      </ListItemButton>
                    ))}
                  </List>
                </Collapse>
              </Fragment>
            );
          })}
        </List>
      </Box>
    </>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      <AppBar
        position="fixed"
        color="default"
        elevation={1}
        sx={{
          zIndex: (t) => t.zIndex.drawer + 1,
          bgcolor: 'background.paper',
          borderBottom: (t) => `1px solid ${t.palette.divider}`,
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            edge="start"
            aria-label="open navigation menu"
            onClick={() => setMobileOpen((o) => !o)}
            sx={{ mr: 1, display: { md: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          <Box
            component="img"
            src={logo}
            alt="Dikonek"
            sx={{ height: 36, width: 'auto', mr: 1.5, display: 'block' }}
          />
          <Typography variant="h6" noWrap sx={{ flexGrow: 1, fontWeight: 700 }}>
            Projects
          </Typography>
          <Typography variant="body2" sx={{ opacity: 0.85, mr: 1 }}>
            {me?.user?.name ?? user?.username ?? 'dev'}
          </Typography>
          <IconButton
            color="inherit"
            onClick={(e) => setUserMenuEl(e.currentTarget)}
            aria-label="account menu"
          >
            <AccountCircleIcon />
          </IconButton>
          <Menu anchorEl={userMenuEl} open={Boolean(userMenuEl)} onClose={() => setUserMenuEl(null)}>
            <MenuItem onClick={onLogout}>
              <ListItemIcon>
                <LogoutIcon fontSize="small" />
              </ListItemIcon>
              Logout
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>
      <Box
        component="nav"
        sx={{ width: { md: DRAWER_WIDTH }, flexShrink: { md: 0 } }}
        aria-label="navigation"
      >
        {/* Mobile: temporary drawer toggled by the hamburger — slides over content */}
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={() => setMobileOpen(false)}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', md: 'none' },
            [`& .MuiDrawer-paper`]: { width: DRAWER_WIDTH, boxSizing: 'border-box' },
          }}
        >
          {drawerContent}
        </Drawer>
        {/* Desktop: permanent docked drawer */}
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', md: 'block' },
            [`& .MuiDrawer-paper`]: { width: DRAWER_WIDTH, boxSizing: 'border-box' },
          }}
          open
        >
          {drawerContent}
        </Drawer>
      </Box>
      <Box component="main" sx={{ flexGrow: 1, minWidth: 0, width: { md: `calc(100% - ${DRAWER_WIDTH}px)` } }}>
        <Toolbar />
        {cap?.canApprove && <KillSwitchBanner />}
        <Box sx={{ p: { xs: 1.5, sm: 3 } }}>{children}</Box>
      </Box>
    </Box>
  );
}
